import React, {useState} from 'react'
import PropTypes from 'prop-types'
import MinicartTotal from './minicartTotal'
import MinicartContent from './minicartContent'
import builderConfig from 'hec/builder/model-map'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Content} from '@builder.io/sdk-react'
import {customComponents} from 'hec/builder'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import {Button, buttonFunctions} from '@/components/cta'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import modelMap from 'hec/builder/model-map'
import {IBuilderPageReference} from 'hec/types/common'
import useMinicart from './useMinicart'

const Minicart = ({
    emptyMinicartContent,
    attributes,
    pageReference,
    checkoutPageReference,
    splitCTAs,
    'data-testid': dataTestId
}) => {
    const minicartData = useMinicart()
    const {totalItems, basket, adjustedOrderTotal} = minicartData
    const config = getConfig()
    const [open, setOpen] = useState(false)

    const handleMouseEnter = () => {
        setOpen(true)
    }

    const handleMouseLeave = () => {
        setOpen(false)
    }

    const shouldQueryArticleTopic = !pageReference?.value?.data

    const {data: emptyMinicartData, isError: isEmptyMinicartError} = useFetchBuilderReference({
        model: builderConfig.popoverContent,
        shouldQuery: true,
        referenceId: emptyMinicartContent?.id
    })

    const {data: cartPageData} = useFetchBuilderReference({
        model: modelMap.pageModel,
        shouldQuery: shouldQueryArticleTopic,
        referenceId: pageReference?.id,
        fields: 'data'
    })

    const {data: checkoutPageData} = useFetchBuilderReference({
        model: modelMap.pageModel,
        shouldQuery: true,
        referenceId: checkoutPageReference?.id,
        fields: 'data'
    })

    const cartPageRef = pageReference?.value?.data || cartPageData?.data
    const checkoutPageRef = checkoutPageReference?.value?.data || checkoutPageData?.data

    const {url, routeType} = useDetermineRouteTypeAndUrl({
        clickFunction: buttonFunctions.linkToPage,
        isInternalLink: true,
        pageReference: cartPageRef
    })
    if (isEmptyMinicartError && totalItems === 0) {
        console.error(
            'An error occurred while fetching an empty minicart data: ',
            isEmptyMinicartError
        )

        return null
    }

    return (
        <div {...attributes}>
            <Popover open={open} onOpenChange={setOpen} data-testid="minicart-Popover">
                <PopoverTrigger
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    data-testid="minicart-PopoverTrigger"
                >
                    <Button
                        ctaType="link"
                        url={url}
                        routeType={routeType}
                        clickFunction={buttonFunctions.linkToPage}
                        data-testid={dataTestId || 'minicart-Link'}
                    >
                        <MinicartTotal
                            active={open}
                            basket={basket}
                            totalItems={totalItems}
                            adjustedOrderTotal={adjustedOrderTotal}
                            data-testid="minicart-MinicartTotal"
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    alignOffset={0}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className="w-[calc(100vw-1rem)] sm:w-[23.4375rem] h-auto p-0"
                    data-testid="minicart-PopoverContent"
                >
                    {totalItems > 0 ? (
                        <MinicartContent
                            minicartData={minicartData}
                            cartPageRef={cartPageRef}
                            splitCTAs={splitCTAs}
                            checkoutPageRef={checkoutPageRef}
                            data-testid="minicart-MinicartContent"
                        />
                    ) : (
                        emptyMinicartData && (
                            <Content
                                model={builderConfig.popoverContent}
                                content={emptyMinicartData}
                                apiKey={config.app.builder.api}
                                customComponents={customComponents}
                                data-testid="minicart-Content"
                            />
                        )
                    )}
                </PopoverContent>
            </Popover>
        </div>
    )
}

export default Minicart

Minicart.propTypes = {
    emptyMinicartContent: PropTypes.object.isRequired,
    attributes: PropTypes.object.isRequired,
    checkoutPageReference: IBuilderPageReference,
    pageReference: PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.shape({
            data: PropTypes.shape({
                isSfraPage: PropTypes.bool,
                url: PropTypes.string
            })
        })
    }),
    splitCTAs: PropTypes.bool,
    'data-testid': PropTypes.string
}

export const MinicartDefinition = {
    component: Minicart,
    name: 'Minicart',
    noWrap: true,
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/shopping-bag.svg',
    inputs: [
        {
            name: 'emptyMinicartContent',
            friendlyName: 'Empty Minicart Content',
            type: 'reference',
            model: builderConfig.popoverContent
        },
        {
            name: 'pageReference',
            friendlyName: 'Cart page reference',
            type: 'reference',
            model: 'page'
        },
        {
            name: 'checkoutPageReference',
            friendlyName: 'Checkout page reference',
            type: 'reference',
            model: 'page'
        },
        {
            name: 'splitCTAs',
            friendlyName: 'Enable split CTAs',
            type: 'boolean',
            defaultValue: false,
            description:
                'If true, the minicart will display separate buttons for checkout and cart.'
        }
    ]
}
